/**
 * useChartGenerator - Hook per generare grafici con Chart.js nel browser
 * Il Diario di Bordo - Frontend
 * 
 * Genera grafici come immagini base64 da inviare al backend per il PDF
 * GDPR Compliant - I dati restano tra browser e server
 */

import { useCallback, useRef } from 'react'
import Chart from 'chart.js/auto'

// Colori standard (uguali al backend)
const COLORS = {
  primary: '#2563eb',
  success: '#22c55e',
  danger: '#ef4444',
  warning: '#f59e0b',
  purple: '#8b5cf6',
  gray: '#6b7280',
  lightGray: '#e5e7eb'
}

/**
 * Hook per generare grafici
 */
export function useChartGenerator() {
  const chartInstancesRef = useRef({})

  /**
   * Genera un singolo grafico e ritorna base64
   */
  const generateChart = useCallback(async (config, width = 500, height = 300) => {
    return new Promise((resolve) => {
      // Crea canvas temporaneo
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      
      // Crea contesto
      const ctx = canvas.getContext('2d')
      
      // Configura per alta qualità
      const dpr = window.devicePixelRatio || 1
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = width + 'px'
      canvas.style.height = height + 'px'
      ctx.scale(dpr, dpr)
      
      // Crea grafico
      const chart = new Chart(ctx, {
        ...config,
        options: {
          ...config.options,
          responsive: false,
          animation: false
        }
      })
      
      // Aspetta che il grafico sia renderizzato
      setTimeout(() => {
        const base64 = canvas.toDataURL('image/png')
        chart.destroy()
        resolve(base64)
      }, 100)
    })
  }, [])

  /**
   * Genera grafico lineare comportamenti
   */
  const generateBehaviorLineChart = useCallback(async (data) => {
    const config = {
      type: 'line',
      data: {
        labels: data.labels || [],
        datasets: [
          {
            label: 'Positivi',
            data: data.positivi || [],
            borderColor: COLORS.success,
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: COLORS.success
          },
          {
            label: 'Negativi',
            data: data.negativi || [],
            borderColor: COLORS.danger,
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: COLORS.danger
          },
          {
            label: 'Neutri',
            data: data.neutri || [],
            borderColor: COLORS.gray,
            backgroundColor: 'rgba(107, 114, 128, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: COLORS.gray
          }
        ]
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: 'Andamento Comportamenti',
            font: { size: 14, weight: 'bold' }
          },
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true }
          }
        },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1 } }
        }
      }
    }
    
    return generateChart(config)
  }, [generateChart])

  /**
   * Genera grafico a torta comportamenti
   */
  const generateBehaviorPieChart = useCallback(async (data) => {
    const config = {
      type: 'doughnut',
      data: {
        labels: ['Positivi', 'Negativi', 'Neutri'],
        datasets: [{
          data: [data.positivi || 0, data.negativi || 0, data.neutri || 0],
          backgroundColor: [COLORS.success, COLORS.danger, COLORS.gray],
          borderWidth: 2,
          borderColor: 'white'
        }]
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: 'Distribuzione Comportamenti',
            font: { size: 14, weight: 'bold' }
          },
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true }
          }
        }
      }
    }
    
    return generateChart(config)
  }, [generateChart])

  /**
   * Genera grafico a barre presenze
   */
  const generateAttendanceBarChart = useCallback(async (data) => {
    const config = {
      type: 'bar',
      data: {
        labels: data.labels || [],
        datasets: [
          {
            label: 'Presenze',
            data: data.presenze || [],
            backgroundColor: COLORS.success,
            borderRadius: 4
          },
          {
            label: 'Assenze',
            data: data.assenze || [],
            backgroundColor: COLORS.danger,
            borderRadius: 4
          }
        ]
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: 'Presenze per Periodo',
            font: { size: 14, weight: 'bold' }
          },
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true }
          }
        },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1 } }
        }
      }
    }
    
    return generateChart(config)
  }, [generateChart])

  /**
   * Genera grafico stats cards (barre orizzontali)
   */
  const generateStatsChart = useCallback(async (data) => {
    const config = {
      type: 'bar',
      data: {
        labels: ['Presenze', 'Assenze', 'Giustificati'],
        datasets: [{
          data: [data.presenti || 0, data.assenti || 0, data.giustificati || 0],
          backgroundColor: [COLORS.success, COLORS.danger, COLORS.warning],
          borderRadius: 6
        }]
      },
      options: {
        indexAxis: 'y',
        plugins: {
          title: {
            display: true,
            text: `Riepilogo Presenze - ${data.percentuale || 0}% frequenza`,
            font: { size: 14, weight: 'bold' }
          },
          legend: { display: false }
        },
        scales: {
          x: { beginAtZero: true, ticks: { stepSize: 1 } }
        }
      }
    }
    
    return generateChart(config, 500, 180)
  }, [generateChart])

  /**
   * Genera grafico attività (doughnut)
   */
  const generateActivityChart = useCallback(async (data) => {
    const colors = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger,
                    COLORS.purple, '#ec4899', '#06b6d4', '#84cc16']
    
    const config = {
      type: 'doughnut',
      data: {
        labels: data.labels || [],
        datasets: [{
          data: data.values || [],
          backgroundColor: colors.slice(0, (data.labels || []).length),
          borderWidth: 2,
          borderColor: 'white'
        }]
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: 'Distribuzione Attività',
            font: { size: 14, weight: 'bold' }
          },
          legend: {
            position: 'right',
            labels: { usePointStyle: true }
          }
        }
      }
    }
    
    return generateChart(config)
  }, [generateChart])

  /**
   * Genera tutti i grafici necessari per un report
   * @param {Object} compilationData - Dati dal backend
   * @param {Object} options - Opzioni (es. tipo grafico comportamenti: 'line' o 'pie')
   * @returns {Object} - Oggetto con tutti i grafici in base64
   */
  const generateAllCharts = useCallback(async (compilationData, options = {}) => {
    const charts = {}
    
    try {
      // Grafico comportamenti
      if (compilationData.comportamenti) {
        if (options.behaviorChartType === 'pie') {
          charts.comportamenti = await generateBehaviorPieChart({
            positivi: compilationData.comportamenti.totals?.positivi || 0,
            negativi: compilationData.comportamenti.totals?.negativi || 0,
            neutri: compilationData.comportamenti.totals?.neutri || 0
          })
        } else {
          charts.comportamenti = await generateBehaviorLineChart({
            labels: compilationData.comportamenti.trend?.labels || [],
            positivi: compilationData.comportamenti.trend?.positivi || [],
            negativi: compilationData.comportamenti.trend?.negativi || [],
            neutri: compilationData.comportamenti.trend?.neutri || []
          })
        }
      }
      
      // Grafico presenze
      if (compilationData.presenze) {
        // Barre presenze per periodo
        if (compilationData.presenze.byPeriod) {
          charts.presenze = await generateAttendanceBarChart({
            labels: compilationData.presenze.byPeriod.labels || [],
            presenze: compilationData.presenze.byPeriod.presenze || [],
            assenze: compilationData.presenze.byPeriod.assenze || []
          })
        }
        
        // Stats cards
        charts.statsCards = await generateStatsChart({
          presenti: compilationData.presenze.presenti || 0,
          assenti: compilationData.presenze.assenti || 0,
          giustificati: compilationData.presenze.giustificati || 0,
          percentuale: compilationData.presenze.percentuale || 0
        })
      }
      
      // Grafico attività
      if (compilationData.attivita?.distribution) {
        charts.attivita = await generateActivityChart({
          labels: compilationData.attivita.distribution.labels || [],
          values: compilationData.attivita.distribution.values || []
        })
      }
      
    } catch (error) {
      console.error('Error generating charts:', error)
    }
    
    return charts
  }, [
    generateBehaviorLineChart, 
    generateBehaviorPieChart, 
    generateAttendanceBarChart, 
    generateStatsChart, 
    generateActivityChart
  ])

  return {
    generateChart,
    generateBehaviorLineChart,
    generateBehaviorPieChart,
    generateAttendanceBarChart,
    generateStatsChart,
    generateActivityChart,
    generateAllCharts,
    COLORS
  }
}

export default useChartGenerator
